import type { DateRecord } from '~/builders/project-dates/DateBuilder';
import type { Configuration, MillageInformation, TaxableValueMap } from '~/types';
import type { DateRecordsMapByPaymentDate } from '~/types/project';
import { calculateProjectTimeline } from '~/utils/project-timeline';
import { generateLinearShape, LINEAR_BUILDS } from '../shapes/linear';
import { generateLogAccelShape, LOG_ACCEL_BUILDS } from '../shapes/log-accel';
import { generateLogDecelShape, LOG_DECEL_BUILDS } from '../shapes/log-decel';
import { MASTER_SHAPE_LIST } from '../shapes/shape-registry';

// For demo purposes, we'll create a simplified project data structure
export interface SimplifiedProjectData {
  dateRecords: DateRecord[];
  dateRecordsMapByPaymentDate: DateRecordsMapByPaymentDate;
  taxableValueMap: TaxableValueMap;
  // Add date inputs for timeline calculation
  dateInputs?: {
    startingTaxYear: number;
  };
}

/**
 * Generates a specific MillageInformation based on project information and shape type
 * @param projectData The project data (simplified for demo)
 * @param configuration The candidate configuration
 * @param shapeId The id of the shape to generate (e.g., 'log-accel-01', 'linear-01')
 * @returns MillageInformation object for the specified shape
 */
export function generateSpecificShape(
  projectData: SimplifiedProjectData,
  configuration: Configuration,
  shapeId: string
): MillageInformation {
  const { millageInputs, bondSeries } = configuration;

  if (!millageInputs) {
    throw new Error('Millage inputs not found in configuration');
  }

  const shapeInfo = MASTER_SHAPE_LIST.find((s) => s.id === shapeId);
  if (!shapeInfo) {
    throw new Error(`Shape with id "${shapeId}" not found in master list.`);
  }

  // Calculate project timeline from actual project data
  let startYear: number;
  let endYear: number;

  if (projectData.dateInputs?.startingTaxYear && projectData.dateRecordsMapByPaymentDate) {
    // Use actual project timeline calculation
    const timeline = calculateProjectTimeline(
      projectData.dateInputs.startingTaxYear,
      bondSeries,
      projectData.dateRecordsMapByPaymentDate
    );
    startYear = timeline.startYear;
    endYear = timeline.endYear;
  } else {
    // Fallback to demo timeline if project data is incomplete
    console.warn('Using fallback timeline calculation - project data incomplete');
    startYear = new Date().getFullYear();
    endYear = startYear + 20;
  }

  let millageMap: Record<number, number> = {};
  let shapeType: 'linear' | 'log-accel' | 'log-decel' = 'linear';

  // Generate millage values based on shape family
  if (shapeInfo.family === 'linear') {
    const build = LINEAR_BUILDS.find((b) => b.id === shapeId);
    if (!build) throw new Error(`Linear build ${shapeId} not found`);

    const shapeResult = generateLinearShape({
      build,
      millageInputs,
      startYear,
      endYear,
    });

    millageMap = Object.fromEntries(
      Object.entries(shapeResult).map(([year, data]) => [parseInt(year, 10), data.millage])
    );
    shapeType = 'linear';
  } else if (shapeInfo.family === 'log-accel') {
    const build = LOG_ACCEL_BUILDS.find((b) => b.id === shapeId);
    if (!build) throw new Error(`Log-accel build ${shapeId} not found`);

    const shapeResult = generateLogAccelShape({
      build,
      millageInputs,
      startYear,
      endYear,
    });

    millageMap = Object.fromEntries(
      Object.entries(shapeResult).map(([year, data]) => [parseInt(year, 10), data.millage])
    );
    shapeType = 'log-accel';
  } else if (shapeInfo.family === 'log-decel') {
    const build = LOG_DECEL_BUILDS.find((b) => b.id === shapeId);
    if (!build) throw new Error(`Log-decel build ${shapeId} not found`);

    const shapeResult = generateLogDecelShape({
      build,
      millageInputs,
      startYear,
      endYear,
    });

    millageMap = Object.fromEntries(
      Object.entries(shapeResult).map(([year, data]) => [parseInt(year, 10), data.millage])
    );
    shapeType = 'log-decel';
  }

  // Validate that millageMap was populated
  if (Object.keys(millageMap).length === 0) {
    throw new Error(
      `Failed to generate millage map for shape ${shapeId}. ` +
        `Shape family: ${shapeInfo.family}, ` +
        `Timeline: ${startYear}-${endYear}. ` +
        `This usually indicates a problem with the shape generator or missing millage inputs.`
    );
  }

  // Calculate summary statistics
  const millageValues = Object.values(millageMap);
  if (millageValues.length === 0) {
    throw new Error(`Millage values array is empty for shape ${shapeId} after map generation`);
  }

  const totalRevenue = millageValues.reduce((sum, millage) => sum + millage * 1000000, 0); // Demo calculation
  const peakMillage = Math.max(...millageValues);
  const averageMillage =
    millageValues.reduce((sum, millage) => sum + millage, 0) / millageValues.length;
  const millageUtilization = averageMillage / peakMillage;

  // Validate calculated values
  if (
    !Number.isFinite(peakMillage) ||
    !Number.isFinite(averageMillage) ||
    !Number.isFinite(millageUtilization)
  ) {
    throw new Error(
      `Invalid millage statistics calculated for shape ${shapeId}: ` +
        `peakMillage=${peakMillage}, averageMillage=${averageMillage}, millageUtilization=${millageUtilization}`
    );
  }

  // Create intervals for the shape
  const intervals = [
    {
      startYear,
      endYear,
      startMillage: millageValues[0] || 0,
      endMillage: millageValues[millageValues.length - 1] || 0,
      intervalType: 'linear' as const,
    },
  ];

  const millageShape = {
    shapeId,
    shapeName: shapeInfo.family,
    shapeType,
    intervals,
    adjustments: [], // No adjustments for now
  };

  // Final validation before return
  if (!millageShape || !millageShape.shapeId) {
    throw new Error(`Failed to create millageShape object for shape ${shapeId}`);
  }

  return {
    millageShape,
    millageMap,
    totalRevenue,
    peakMillage,
    averageMillage,
    millageUtilization,
  };
}
